import EXIF from 'exif-js'
import Compressor from 'compressorjs'

import Geographic from '@/lib/geographic'

// 20mb
const IMAGE_SIZE_LIMIT = 20_000_000

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png']

export const INVALID_IMAGE_TYPE_MESSAGE = 'Photos must be JPEG or PNG files.'

interface ExifData {
  GPSLatitude: [number, number, number]
  GPSLatitudeRef: string
  GPSLongitude: [number, number, number]
  GPSLongitudeRef: string
}

export interface ExifFile extends File {
  exifdata: ExifData
}

interface Photo {
  client_created_at: string
}

/**
 * Make sure the file is under the limit
 *
 * @param image - image file
 * @return if the image size is valid
 */
export const checkImageSize = (image: { size: number }): boolean => {
  // 20mb limit
  return image.size <= IMAGE_SIZE_LIMIT
}

/**
 * Make sure the file is a JPEG or PNG
 *
 * @param image - image file
 * @return if the image type is valid
 */
export const isAcceptedImageType = (image: { type: string }): boolean => {
  return ACCEPTED_IMAGE_TYPES.includes(image.type)
}

/**
 * Compress image
 *
 * @param image - image
 * @param options - additional options
 * @return promise with the compressed image
 */
export const compressImage = (
  image: File | Blob,
  options: Compressor.Options = {}
): Promise<File | Blob> => {
  const COMPRESSOR_QUALITY = 0.4
  // 2mb
  const COMPRESSOR_TARGET_SIZE = 2_000_000

  return new Promise((resolve, reject) => {
    new Compressor(image, {
      quality: COMPRESSOR_QUALITY,
      convertSize: COMPRESSOR_TARGET_SIZE,
      ...options,
      success: (response) => {
        resolve(response)
      },
      error: (err) => {
        reject(err)
      },
    })
  })
}

/**
 * Get location info from photo EXIF data
 *
 * @param file - the HTML file object containing the photo
 */
export const getLocationFromPhoto = (
  file: File
): Promise<Geographic | null> => {
  return new Promise((resolve) =>
    EXIF.getData(file as unknown as string, resolve)
  )
    .then(() => {
      const exifFile = file as unknown as ExifFile
      return Geographic.fromDmsChecked(
        exifFile.exifdata.GPSLongitude[0],
        exifFile.exifdata.GPSLongitude[1],
        exifFile.exifdata.GPSLongitude[2],
        exifFile.exifdata.GPSLongitudeRef,
        exifFile.exifdata.GPSLatitude[0],
        exifFile.exifdata.GPSLatitude[1],
        exifFile.exifdata.GPSLatitude[2],
        exifFile.exifdata.GPSLatitudeRef
      )
    })
    .catch(() => null)
}

/**
 * Sort handler for an array of photos by client created at date. To be used
 * with the Array.sort() function
 *
 * @param photo1 - first photo
 * @param photo2 - second photo
 * @return -1 or 1
 */
export const sortPhotosByCreated = (photo1: Photo, photo2: Photo): number => {
  return photo1.client_created_at >= photo2.client_created_at ? 1 : -1
}

/**
 * Function to check the file type for a photo
 *
 * @param file - file from input or drop event
 * @returns the original file if it is an acceptable type
 */
export const parseImageFile = (file: File): File => {
  // Check if file exists
  if (!file) {
    throw Error('Invalid file')
  }
  // Check to make sure the file is an acceptable type
  if (!isAcceptedImageType(file)) {
    throw Error('File must be a .jpeg or .png')
  }
  return file
}
